/**
 * A reader for the AI SDK UI-message stream `POST /api/embed/chat` answers
 * with - the wire `useChat` reads, without `ai` on the page. Server-Sent
 * Events, one JSON chunk per `data:` line, `data: [DONE]` at the end
 * (`ai/docs/04-ai-sdk-ui/50-stream-protocol.mdx`). The server writes one
 * text part per paragraph (`text-start` / `text-delta` / `text-end`), so
 * the reply is gathered into that shape: a message id and its paragraphs.
 * Anything else the protocol can carry (reasoning, tools, files) the
 * embed's server never sends and this ignores.
 */

export interface Reply {
  id: string
  /** The reply's text parts, in order - one per paragraph. */
  parts: string[]
}

type Chunk =
  | { type: "start"; messageId?: string }
  | { type: "text-start"; id: string }
  | { type: "text-delta"; id: string; delta: string }
  | { type: "text-end"; id: string }
  | { type: "error"; errorText: string }
  | { type: "abort"; reason?: string }
  | { type: string }

export async function readReplyStream(
  body: ReadableStream<Uint8Array>
): Promise<Reply> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let id = ""
  const order: string[] = []
  const texts = new Map<string, string>()

  const take = (line: string) => {
    if (!line.startsWith("data:")) return
    const data = line.slice(5).trim()
    if (!data || data === "[DONE]") return
    let chunk: Chunk
    try {
      chunk = JSON.parse(data) as Chunk
    } catch {
      return
    }
    switch (chunk.type) {
      case "start":
        if ("messageId" in chunk && chunk.messageId) id = chunk.messageId
        break
      case "text-start":
        if ("id" in chunk) {
          order.push(chunk.id)
          texts.set(chunk.id, "")
        }
        break
      case "text-delta":
        if ("id" in chunk && "delta" in chunk) {
          texts.set(chunk.id, (texts.get(chunk.id) ?? "") + chunk.delta)
        }
        break
      case "error":
        throw new Error(
          "errorText" in chunk ? chunk.errorText : "The reply failed."
        )
      case "abort":
        throw new Error("The reply was cut short.")
    }
  }

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let at: number
    while ((at = buffer.indexOf("\n")) !== -1) {
      take(buffer.slice(0, at).replace(/\r$/, ""))
      buffer = buffer.slice(at + 1)
    }
  }
  if (buffer) take(buffer)

  return {
    id: id || `reply-${Date.now()}`,
    parts: order.map((key) => texts.get(key) ?? ""),
  }
}
